import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  Signal,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  NavigationService,
  StatusLabelComponent,
  StatusLabelTag,
  TableColumn,
  TableComponent,
  TableOptions,
  ToastService,
} from '@h5s/design-system';
import {
  Customer,
  GetIstatCustomerRequest,
  IstatCustomer,
  IstatMode,
  IstatSendStatus,
  IstatStatus,
} from '@h5s/entities';
import { SaveProfileRegistryEvent } from '@h5s/feature-profile-registry';
import { MenuItem } from '@h5s/util-button';
import { EventBusService } from '@h5s/util-event';
import { TranslationService } from '@h5s/util-translate';
import { catchError, EMPTY, filter, map, startWith, switchMap, tap, throwError } from 'rxjs';
import { FeatureIstatService } from '../../../feature-istat.service';
import { SendStatusTooltip } from '../../model/send-status-tooltip';
import { FeatureIstatStore } from '../../store/feature-istat.store';
import { FEATURE_ISTAT_TRANSLATIONS } from '../../translations/feature-istat.translations';
import { IstatFilterModel } from '../istat-filter/models/istat-filter-model';
import { TableIstatCustomer } from './models/table-istat-customer';
import { TooltipValidationError } from './models/tooltip-validation-error';

const DESIGN_SYSTEM = [TableComponent, StatusLabelComponent];

/**
 * Eventi gestiti:
 *
 * SaveProfileRegistryEvent
 */
@Component({
  selector: 'h5s-istat-table',
  imports: [ReactiveFormsModule, ...DESIGN_SYSTEM],
  templateUrl: './istat-table.component.html',
  styleUrl: './istat-table.component.scss',
})
export class IstatTableComponent implements OnInit {
  private readonly featureIstatService = inject(FeatureIstatService);
  private readonly navigationService = inject(NavigationService);
  private readonly toastService = inject(ToastService);
  protected translationService = inject(TranslationService);
  private readonly featureIstatStore = inject(FeatureIstatStore);
  private readonly eventBusService = inject(EventBusService);
  private readonly destroyRef = inject(DestroyRef);

  /** Traduzioni */
  public readonly translation = FEATURE_ISTAT_TRANSLATIONS;

  /** Template */
  private readonly istatCompleteStatus = viewChild<TemplateRef<unknown>>('istatCompleteStatus');
  private readonly istatSendStatus = viewChild<TemplateRef<unknown>>('istatSendStatus');

  /*Variabili*/
  public readonly tableSelectedRecords = signal<TableIstatCustomer[]>([]);
  public isIstatTypePrintable = this.featureIstatStore.isSelectedIstatTypePrintable;
  public incompleteCustomer = (row: TableIstatCustomer): boolean => !this.shouldBeChecked(row);
  public readonly loading = signal<boolean>(false);

  public readonly filters = input<IstatFilterModel | null>(null);

  public readonly tableIstatCustomers: Signal<TableIstatCustomer[]> = toSignal(
    toObservable(this.filters).pipe(
      filter((filters): filters is IstatFilterModel => !!filters),
      tap((filters) => {
        this.featureIstatStore.updateSelectedFilters(filters);
      }),
      switchMap((filters) =>
        this.featureIstatService.research.pipe(
          map(() => filters),
          startWith(filters),
        ),
      ),
      tap(() => {
        this.loading.set(true);
      }),
      switchMap((filters) => {
        if (!filters.profile) {
          this.loading.set(false);
          return EMPTY;
        }

        const request = new GetIstatCustomerRequest({
          ownerIstatId: filters.profile,
          year: filters.year ?? null,
          month: filters.month ?? null,
          referenceDate: filters.day ?? null,
          beginDate: filters.startDate ?? null,
          endDate: filters.endDate ?? null,
        });

        return this.featureIstatService.getFilteredIstatCustomers(request);
      }),
      map((customers: IstatCustomer[]) => customers.map((customer) => new TableIstatCustomer(customer))),
      tap(() => {
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        this.toastService.showError(this.translation.errorLoadData);
        return throwError(() => error);
      }),
    ),
    { initialValue: [] },
  );

  //-- Tabella ---------------------------------------

  /** Colonne della tabella */
  public readonly tableColumns = computed<TableColumn<TableIstatCustomer>[]>(() => {
    const columns: TableColumn<TableIstatCustomer>[] = [
      {
        key: 'isValid',
        header: this.translation.completeHeader,
        showAs: 'template',
        customTemplate: this.istatCompleteStatus(),
      },
      {
        key: 'istatCustomerMode',
        header: this.translation.inOutHeader,
        showAs: 'template',
        getCellDisplayValue: (r: TableIstatCustomer): string =>
          this.featureIstatService.getIstatCustomerMode(r.istatCustomerMode),
      },
    ];

    if (this.showState()) {
      columns.push({
        key: 'istatSendStatus',
        header: this.translation.stateHeader,
        customTemplate: this.istatSendStatus(),
      });
    }

    columns.push(
      {
        key: 'name',
        header: this.translation.nameHeader,
        getCellDisplayValue: (r: TableIstatCustomer): string => r.getDisplayName(),
      },
      {
        key: 'relatives',
        header: this.translation.groupMemberHeader,
        getCellDisplayValue: (r: TableIstatCustomer): string => {
          const relatives = r.relativePurchasedStayToCustomers ?? [];
          return relatives
            .map((purchasedStayToCustomer) => {
              const customer = new Customer(purchasedStayToCustomer.customer);
              return customer.getDisplayName();
            })
            .join(', ');
        },
      },
      {
        key: 'guestsNumber',
        header: this.translation.guestsNumberHeader,
        getCellDisplayValue: (r: TableIstatCustomer): number => r.guestsNumber,
        showTotal: true,
      },
      {
        key: 'city',
        header: this.translation.nationalityHeader,
        getCellDisplayValue: (r: TableIstatCustomer): string => r.city ?? '',
      },
      {
        key: 'room',
        header: this.translation.roomHeader,
        getCellDisplayValue: (r: TableIstatCustomer): string => r.room,
      },
    );

    return columns;
  });

  /** Opzioni tabella */
  public readonly tableOptions = computed<TableOptions<TableIstatCustomer>>(() => ({
    isEditable: false,
    enableSelection: true,
    selectionMode: 'multiple',
    enablePagination: false,
    showActionOnHover: true,
    rowCheckboxDisabled: (row) => !this.shouldBeChecked(row),
    disableRowClick: (row) => !this.shouldBeChecked(row),
    // I filtri sono già popolati all'ingresso: finché non è stata fatta una ricerca lo si segnala
    customEmptyTableMessage: this.filters() ? undefined : this.translation.noSearchPerformedMessage,
    secondaryRowActions: [
      {
        description: this.translation.rowActionCustomerDetail,
        icon: 'uil uil-user',
        action: (row): void => {
          if (row) this.navigationService.changeTab(`/customer/edit/${row.customerId}`);
        },
      },
      {
        description: this.translation.rowActionFamilyMember,
        icon: 'uil uil-users-alt',
        isHidden: (row): boolean => (row ? row.relatives.length === 0 : true),
        getSubactions: (row): MenuItem<TableIstatCustomer | undefined>[] => {
          return (
            row?.relatives.map((customer) => ({
              description: customer.getDisplayName(),
              icon: 'uil uil-user',
              action: (): void => {
                this.navigationService.changeTab(`/customer/edit/${customer.id}`);
              },
            })) ?? []
          );
        },
      },
      {
        description: this.translation.rowActionReservationDetail,
        icon: 'uil uil-bag',
        action: (row): void => {
          const reservationId = row?.reservationId;
          this.navigationService.changeTab(`/reservation/edit/${reservationId}`);
        },
      },
    ],
  }));

  showState = this.featureIstatStore.showStatusColumn;

  constructor() {
    /**
     * Inizializza TableSelectedRecords quando tableIstatCustomers è popolata
     */
    effect(() => {
      if (this.tableIstatCustomers()) this.setTableSelectedRecords();
    });

    /**
     * Aggiorna tableSelectedRecords sullo store
     */
    effect(() => {
      const current = this.tableSelectedRecords();
      this.featureIstatStore.updateSelectedTableIstatCustomer(current);
    });

    /**
     * Carica i parametri di generazione che andranno a popolare la modale e che saranno
     * utilizzati per la send/genera istat
     */
    effect(() => {
      const OwnerIstatProfile = this.featureIstatStore.selectedOwnerIstatProfileId();
      if (OwnerIstatProfile) this.loadGenerationParametersFromStore();
    });

    this.featureIstatService.selectReadyIstatCustomer.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setTableSelectedRecords();
    });
  }

  public ngOnInit(): void {
    this.eventBusService
      .ofFiltered(SaveProfileRegistryEvent, (payload: SaveProfileRegistryEvent) =>
        this.tableIstatCustomers().some((r) => r.customerId === payload.profileRegistryId),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.featureIstatService.research.next();
      });
  }

  /**
   * Caricamento dei parametri dallo store
   */
  private loadGenerationParametersFromStore(): void {
    this.featureIstatStore.loadGenerationParameters().subscribe();
  }

  // Get IstatSendStatusConfigObj dato IstatSendStatus
  public getIstatSendStatus(value: IstatSendStatus): { text: string; status: StatusLabelTag } | null {
    return this.featureIstatService.getIstatSendStatusConfig(value);
  }

  /**
   * Seleziona gli elementi della tabella che sono pronti per l'invio
   */
  private setTableSelectedRecords(): void {
    const tableIstatCustomers = this.tableIstatCustomers();
    if (!tableIstatCustomers) return;

    this.tableSelectedRecords.set((tableIstatCustomers ?? []).filter((customer) => this.shouldBeChecked(customer)));
  }

  /**
   * Per ciascun elemento passato controlla se è pronto per l'invio (true) oppure no(false)
   * @param customer
   */
  private shouldBeChecked(customer: TableIstatCustomer): boolean {
    if (!customer.isValid) {
      return false;
    }

    if (this.isIstatTypePrintable()) {
      return true;
    }

    const { istatStatus, istatMode } = customer;

    if (!istatStatus || !istatMode) {
      return true;
    }

    switch (istatMode) {
      case IstatMode.IstatModeNew:
        return IstatStatus.IstatStatusCheckInSentError === istatStatus;

      case IstatMode.IstatModeEdit:
        return IstatStatus.IstatStatusCheckOutSentOk !== istatStatus;

      case IstatMode.IstatModeDelete:
        // At the moment unused, default true
        return true;

      case IstatMode.IstatModeStill:
        return true;

      case IstatMode.IstatModeTax:
        return true;

      default:
        return true;
    }
  }

  /**
   * Gestisce il contenuto del tooltip per la colonna "Stato"
   */
  public getSendIstatTooltip(row: TableIstatCustomer): SendStatusTooltip | undefined {
    return this.featureIstatService.getStatusTooltip(row);
  }

  /**
   * Gestisce gli errori di validazione per un cliente specifico
   */
  public getCustomerErrors(id: string): TooltipValidationError[] {
    const istatCustomers = this.tableIstatCustomers();
    if (!istatCustomers) return [];
    const customer = istatCustomers.find((c) => c.id === id);
    if (!customer) return [];

    // Crea un array con il cliente e i suoi parenti
    const relatedCustomers = [
      new Customer({
        id: customer.customerId,
        surname: customer.surname,
        name: customer.name,
      }),
      ...customer.relatives,
    ];

    return (
      customer.validationResults?.map(({ resourceId, validationErrors }) => {
        const matchedCustomer = relatedCustomers.find((c) => c.id === resourceId);
        return {
          id: resourceId,
          name: matchedCustomer?.getDisplayName() ?? '',
          errors: validationErrors?.map((err) => err.translationKey),
        };
      }) ?? []
    );
  }
}
